/**
 * Fuso do negócio, num lugar só.
 *
 * POR QUE ISTO EXISTE
 * Havia OITO implementações do mesmo conceito: sete cópias de `getTodayUTC3` espalhadas por
 * páginas (DiarioSDR, DiarioCloser, DiarioSalesFarmer, DiarioSocialSeller, RegistroEnvios,
 * RegistroEventos, CompletarVendaGhlTab) e o `hojeSP` de `services/api.js`. Todas hardcodavam o
 * offset como `- 3 * 3600000`.
 *
 * A aritmética manual funcionava, mas hardcode de offset não é fuso: é uma aposta em que o
 * Brasil não volte a ter horário de verão. Se voltar, os pontos, comissões e diários passam a
 * cair no dia errado por uma hora — e a correção teria que ser caçada em oito arquivos. Nomear o
 * fuso (`America/Sao_Paulo`) deixa o runtime resolver a regra, DST incluso.
 *
 * REGRA DE USO
 * - Precisa de "hoje"?                                   → `hojeSP()`
 * - Tem um INSTANTE e quer o dia dele?                    → `diaSP(instante)`
 * - Tem um instante e quer a hora (HH:MM)?                → `horaSP(instante)`
 * - Aritmética sobre data-string YYYY-MM-DD (somar dias)? → NÃO use isto. Ancore a string em
 *   meio-dia (`new Date(s + 'T12:00:00')`) ou em `T00:00:00Z`: sem hora não há fuso, e ancorar
 *   longe da meia-noite impede que qualquer offset empurre o dia.
 *
 * O gêmeo deste arquivo no dashboard é `dashboard-orbion/src/lib/tz.ts`. Os dois precisam
 * concordar sobre o fuso — é a mesma data sendo escrita e lida.
 */

/** Fuso em que o negócio opera. Toda data que uma pessoa lê no desk é neste fuso. */
export const FUSO_NEGOCIO = 'America/Sao_Paulo';

/**
 * Dia (YYYY-MM-DD) em que o instante caiu no fuso do negócio.
 * `en-CA` é o locale que formata exatamente como YYYY-MM-DD — não é escolha estética.
 */
export function diaSP(instante) {
    const d = instante instanceof Date ? instante : new Date(instante);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-CA', { timeZone: FUSO_NEGOCIO });
}

/** Hoje (YYYY-MM-DD) no fuso do negócio. `agora` é injetável para o teste fixar a fronteira. */
export function hojeSP(agora = new Date()) {
    return diaSP(agora);
}

/**
 * Ancora um DIA (`YYYY-MM-DD`) ao meio-dia do fuso do negócio, para gravação em coluna
 * `timestamptz`.
 *
 * POR QUE EXISTE
 * `agendada_em` e `vendido_em` são `timestamptz`, mas o operador escolhe um DIA num
 * `<input type="date">`. Mandar a string crua faz o Postgres resolvê-la na timezone da sessão
 * — UTC no PostgREST —, gravando meia-noite UTC. Lido de volta em `America/Sao_Paulo` isso é
 * 21h do dia ANTERIOR, e `diaSP` devolve o dia errado: o ponto do agendamento cai um dia antes,
 * o dashboard mostra o dia antes, e `reuniaoInstantanea` (que exige `agendada_em === data_reuniao`)
 * deixa de ser reconhecida. Medido em produção em 2026-09-01 nos leads Daniel HONSHO e Helcio
 * Santos: 3 das 139 reuniões `ghl` estavam assim, e todas as 3 vieram desta tela.
 *
 * O meio-dia é o que torna a âncora segura: qualquer offset entre -11 e +12 mantém o mesmo dia
 * civil. O `-03:00` literal NÃO é uma aposta sobre horário de verão (o dia sobreviveria a -02
 * igual) — ele está aqui para o valor gravado ficar byte a byte igual ao que o pipeline do GHL
 * grava em `dashboard-orbion/src/lib/ghl/field-extract.ts` (`${dia}T12:00:00-03:00`). Uma mesma
 * coluna escrita por dois apps com formatos diferentes é como o defeito passa despercebido.
 *
 * Valor que já traz hora (instante completo) passa intacto: só o dia puro precisa de âncora.
 */
export function ancoraMeioDiaSP(dia) {
    if (typeof dia !== 'string') return dia;
    return /^\d{4}-\d{2}-\d{2}$/.test(dia.trim()) ? `${dia.trim()}T12:00:00-03:00` : dia;
}

/**
 * Soma meses a um dia (`YYYY-MM-DD`) **grampeando o dia ao último do mês de destino**.
 *
 * POR QUE EXISTE
 * `setMonth` do JavaScript transborda em silêncio: 31/08 mais um mês vira 01/10, porque
 * 31/09 não existe e a data "vaza" para o mês seguinte. Nos geradores de parcela isso não é
 * curiosidade — é parcela no mês errado. Medido: venda fechada em 31/08 com 12 boletos
 * produzia SETE meses distintos em vez de doze —
 *
 *   2026-08-31  2026-10-01  2026-10-31  2026-12-01  2026-12-31  2027-01-31
 *   2027-03-03  2027-03-31  2027-05-01  2027-05-31  2027-07-01  2027-07-31
 *
 * — deixando setembro, novembro, fevereiro, abril e junho SEM parcela, e dobrando outubro,
 * dezembro, março, maio e julho. Como `data_venda` da parcela vira `data_base` da comissão e
 * daí `mes_pagamento`, a competência saía errada na maioria das linhas.
 *
 * O grampo é a convenção que o mercado usa para vencimento mensal: quem fecha dia 31 recebe
 * dia 30 em novembro e 28 em fevereiro, e volta ao 31 quando o mês comporta.
 *
 * Aritmética em componentes, sem `Date`: `YYYY-MM-DD` é um dia civil, não um instante, e
 * qualquer passagem por `Date` reintroduz fuso onde não havia nenhum.
 */
export function somarMeses(dia, meses) {
    if (typeof dia !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dia.trim())) return dia;
    const [ano, mes, d] = dia.trim().split('-').map(Number);
    const n = Number(meses) || 0;
    const totalMeses = (ano * 12) + (mes - 1) + n;
    const anoDestino = Math.floor(totalMeses / 12);
    const mesDestino = (totalMeses % 12) + 1;
    // Dia 0 do mês seguinte é o último dia do mês de destino — sem tabela de 30/31 nem bissexto.
    const ultimoDia = new Date(Date.UTC(anoDestino, mesDestino, 0)).getUTCDate();
    const diaDestino = Math.min(d, ultimoDia);
    const pad = (v) => String(v).padStart(2, '0');
    return `${anoDestino}-${pad(mesDestino)}-${pad(diaDestino)}`;
}

/** Hora (HH:MM, 24h) do instante no fuso do negócio. */
export function horaSP(instante) {
    const d = instante instanceof Date ? instante : new Date(instante);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-GB', {
        // `hourCycle: 'h23'` explicito, nao `hour12: false`: os dois sao equivalentes desde a
        // mudanca normativa do ECMA-402 (2021), mas engines anteriores caiam em 'h24' e
        // devolviam "24:05" a meia-noite. Isto roda no navegador do usuario.
        timeZone: FUSO_NEGOCIO, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
}
